import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  useContext,
} from "react";
import GoogleMap, { LatLng, LatLngBounds } from "google-maps-react-markers";
import { config } from "../../../../api/config";
import clsx from "clsx";
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM_LEVEL,
  MAP_STYLE,
  Z_INDEX_BASE_LISTING_MARKER,
  Z_INDEX_CLUSTER_MARKER,
  Z_INDEX_HOVER_OR_CLICKED_CLUSTER_MARKER,
  Z_INDEX_HOVER_OR_SELECTED_LISTING_MARKER,
  Z_INDEX_VIEW_STAYS_NEAR_MARKER,
} from "./const";
import {
  AVAILABILITY_CLUSTER_MAP,
  AVAILABILITY_CLUSTER_MAP_MARKER_LIMIT,
  AVAILABLE,
  CLUSTER,
  CLUSTER_MAP_MARKER_LIMIT_VARIANTS,
  CLUSTER_MAP_VARIANTS,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  LODGING_PROMOTIONS,
  LODGING_PROMOTIONS_AVAILABLE,
  LODGING_PROMOTIONS_VARIANTS,
  LIMIT_200,
  LIMIT_30,
  LIMIT_50,
  PREMIER_COLLECTION_EXPERIMENT,
  STAYS_SEARCH,
  useExperiments,
} from "../../../../context/experiments";
import { AvailabilityClusterMapConnectorProps } from "./container";
import { RouteComponentProps } from "react-router-dom";
import {
  BoundingBox,
  CLICKED_LODGING_MAP,
  ClickedLodgingMapEnum,
  ClickedLodgingMapProperties,
  getLodgingTrackingProperties,
  HOTEL_LIST_MAP_CAROUSEL,
  IIdLodgings,
  IResult,
  LocationDescriptorEnum,
  Lodging,
  LodgingCollectionEnum,
  MapFunnelEnum,
  SEARCH_THIS_AREA,
  ViewedCorpRateDescriptorEntryPoints,
} from "redmond";
import { Skeleton } from "@material-ui/lab";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import useSupercluster from "use-supercluster";
import {
  ActionButton,
  HotelAvailabilityMapTooltip,
  Icon,
  IconName,
} from "halifax";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { openHotelDetailsPage } from "./utils";
import { ViewHotelsNearLocationAutocomplete } from "../ViewHotelsNearLocationAutocomplete";
import { MobileViewHotelsNearSearch } from "../AvailabilityMap/components/MobileViewHotelsNearSearch";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { ListingMarker } from "./components/ListingMarker";
import { ClusterMarker } from "./components/ClusterMarker";
import { LocationMarker } from "./components/LocationMarker";
import { LodgingLocation } from "redmond/build";
import { HotelAvailabilityCallState } from "../../reducer/state";
import { useDeviceTypes } from "halifax";
import "./styles.scss";
import { ClientContext } from "../../../../App";
import { useShowPolicyBanner } from "@capone/common";
import { onOpenCompareBarTooltip } from "../../../../utils/events";
import {
  ClusterProperties,
  SuperclusterCluster,
  SuperclusterPoint,
} from "./types";

export interface IAvailabilityClusterMap
  extends AvailabilityClusterMapConnectorProps,
    RouteComponentProps {}

export const AvailabilityClusterMap = (props: IAvailabilityClusterMap) => {
  const {
    lodgings, // The lodgings returned from the avilability polling (initial & followup)
    availabilityCallState, // The state of the current availability call (initial, followup, complete, error)
    listHoveredLodgingId, // The lodging being hovered from the availability list
    listSelectedLodgingId, // The lodging selected from the availability list
    fetchInitialHotelAvailability, // Performs the initial search for stays availability
    history,

    /**
     * The following props are needed for the map marker modals.
     */
    isVentureX,
    searchedNightCount,
    accountReferenceId,
    largestValueAccount,

    /**
     * The following props are used to determine what kind of search was performed:
     *
     * - Search from the availability list searchbar
     * - "Search this area" button on the map
     * - "View stays near" autocomplete input on the map
     */
    searchLocation, // The location of the search (updates in all cases)
    searchCentroids, // The centroids of the search result (unique to each availability call)
    searchMapBound, // The map bounds set by clicking "Search this area" on the map.
    viewHotelsNearLocation, // The location set by using the "View stays near" autocomplete input on the map.
    setSearchMapBound, // Sets the map bounds which is used by fetchInitialHotelAvailability

    /**
     * The following props are required for the map "View stays near" autocomplete
     */
    fetchViewHotelsNearLocationCategories, // Fetches the categories for the autocomplete
    viewHotelsNearAvailabilityProperties, // Needed for the tracking properties
    setViewHotelsNearLocation, // Sets the location for the "View stays near" autocomplete input for mobile

    /**
     * The following props are required to open the hotel details page (openHotelDetailsPage)
     */
    searchLocationResult,
    hotelQueryParams,
    sortOrder,
    mapBounds, // Current bounds of the map
    roomsCount, // The number of rooms from the search
    viewedHotelListProperties, // Tracking properties
  } = props;

  const { matchesDesktop } = useDeviceTypes();

  // Experiments
  const expState = useExperiments();
  const clusterMapVariantExp = getExperimentVariantCustomVariants(
    expState.experiments,
    AVAILABILITY_CLUSTER_MAP,
    CLUSTER_MAP_VARIANTS
  );
  const showClustersVariant = clusterMapVariantExp === CLUSTER;

  /**
   * This experiment only applies when clusterMapVariantExp === NO_CLUSTER
   *
   * It limits the amount of markers to show on the map viewport
   */
  const markerLimitVariantExp = getExperimentVariantCustomVariants(
    expState.experiments,
    AVAILABILITY_CLUSTER_MAP_MARKER_LIMIT,
    CLUSTER_MAP_MARKER_LIMIT_VARIANTS
  );

  let noClusteringMarkerLimit = 999;
  switch (markerLimitVariantExp) {
    case LIMIT_200:
      noClusteringMarkerLimit = 200;
      break;
    case LIMIT_50:
      noClusteringMarkerLimit = 50;
      break;
    case LIMIT_30:
      noClusteringMarkerLimit = 30;
      break;
    default:
      noClusteringMarkerLimit = 999;
  }

  const staysSearchEnabled =
    getExperimentVariant(expState.experiments, STAYS_SEARCH) === AVAILABLE;

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const lodgingPromotions = getExperimentVariantCustomVariants(
    expState.experiments,
    LODGING_PROMOTIONS,
    LODGING_PROMOTIONS_VARIANTS
  );

  const isLodgingPromotionsExperiment =
    lodgingPromotions === LODGING_PROMOTIONS_AVAILABLE;

  // Initializing Map
  const mapRef = useRef<any>(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false); // Ensures the Google map is initialized with the API key
  const [centerCoordsLoaded, setCenterCoordsLoaded] = useState(false); // Ensures the center is parsed from the searchLocation and map pans
  const [initialMapLoaded, setInitialMapLoaded] = useState(false); // Shows the loading skeleleton while map variables are loading
  const [mapMoved, setMapMoved] = useState(false); // Shows the loading skeleleton while map variables are loading
  const [initialSearch, setInitialSearch] = useState(true); // Tracking property. If "Search this area" hasn't been clicked or "View stays near..." has not been used.

  const [currSearchLocation, setCurrSearchLocation] =
    useState<LodgingLocation | null>();
  const [currSearchMapBound, setCurrSearchMapBound] = useState<BoundingBox>();
  const [currViewHotelsNearLocation, setCurrViewHotelsNearLocation] =
    useState<IResult>();

  const [
    mapAutocompleteSearchCoordinates,
    setMapAutocompleteSearchCoordinates,
  ] = useState<LatLng | undefined>(undefined);

  const onGoogleApiLoaded = ({ map }: any) => {
    mapRef.current = map;
    setGoogleMapsLoaded(true);

    // Handling the clean/dirty state of the map in a listener because the map.onChange handler is instance scoped
    map.addListener("drag", () => {
      if (!mapMoved) {
        setMapMoved(true);
      }
    });
  };

  useEffect(() => {
    if (googleMapsLoaded && centerCoordsLoaded) {
      setInitialMapLoaded(true);
    }
  }, [googleMapsLoaded, centerCoordsLoaded]);

  // Map Markers
  const [bounds, setBounds] = useState<any>([]);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM_LEVEL);
  const [hoveredMarker, setHoveredMarker] = useState<string>(); // Used to set the z-index of the hovered marker
  const [hoveredCluster, setHoveredCluster] = useState<number>(); // Used to set the z-index of the hovered cluster
  const [selectedMarker, setSelectedMarker] = useState<string>();
  const [selectedMarkerLodging, setSelectedMarkerLodging] = useState<Lodging>(); // Used for the mobile map tooltip
  const [
    viewHotelsNearMobileSearchOpened,
    setViewHotelsNearMobileSearchOpened,
  ] = React.useState(false);

  // Needed for mobile map modal
  const { policies } = useContext(ClientContext);
  const showPolicyBanner = useShowPolicyBanner(policies);

  const moveMapTo = (lat: number, lng: number, zoom?: number) => {
    if (!mapRef?.current) return;
    if (zoom != null) {
      mapRef.current.setZoom(zoom);
    }
    mapRef.current.panTo({
      lat: lat,
      lng: lng,
    });
  };

  const moveMapToBounds = (bounds: LatLngBounds) => {
    if (!mapRef?.current) return;
    if (zoom != null) {
      mapRef.current.setZoom(zoom);
    }
    mapRef.current.panToBounds(bounds);
  };

  // Set the selected marker when a listing is selected from the list
  useEffect(() => {
    if (listSelectedLodgingId) {
      setSelectedMarker(listSelectedLodgingId);
    }
  }, [listSelectedLodgingId]);

  /**
   * This effect is used to determine the location of the map.
   */
  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    if (
      searchMapBound &&
      currSearchMapBound?.northEast !== searchMapBound?.northEast &&
      currSearchMapBound?.southWest !== searchMapBound?.southWest
    ) {
      // Rare case where the user switches from mobile -> desktop
      if (!centerCoordsLoaded) {
        const bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(
            searchMapBound?.southWest?.lat,
            searchMapBound?.southWest?.lon
          ),
          new google.maps.LatLng(
            searchMapBound?.northEast?.lat,
            searchMapBound?.northEast?.lon
          )
        );
        moveMapToBounds(bounds);
      }

      setMapMoved(false);
      setInitialSearch(false); // The initial search has changed
      setSelectedMarker(undefined); // Reset the selected marker when a new location is selected
      setCenterCoordsLoaded(true);
      setCurrSearchMapBound(searchMapBound);
    } else if (
      viewHotelsNearLocation &&
      currViewHotelsNearLocation?.id !== viewHotelsNearLocation?.id
    ) {
      const geocoder = new google.maps.Geocoder();
      geocoder
        .geocode({
          placeId: (viewHotelsNearLocation.id as IIdLodgings).lodgingSelection
            .placeId,
        })
        .then(({ results }) => {
          setMapAutocompleteSearchCoordinates(results[0].geometry.location);
          moveMapTo(
            results[0].geometry.location.lat(),
            results[0].geometry.location.lng(),
            16
          );
          setMapMoved(false);
          setInitialSearch(false); // The initial search has changed
          setSelectedMarker(undefined); // Reset the selected marker when a new location is selected
          setCurrViewHotelsNearLocation(viewHotelsNearLocation);
          setCenterCoordsLoaded(true);

          trackEvent({
            eventName: "view_hotels_near",
            properties: {
              ...viewHotelsNearAvailabilityProperties,
              map_funnel: MapFunnelEnum.STAYS,
            },
          });
          fetchInitialHotelAvailability({
            history: history,
            searchFromMap: false,
            searchHotelsNear: true,
            includeHomes: staysSearchEnabled,
          });
        });
    } else if (
      !searchMapBound &&
      !viewHotelsNearLocation &&
      searchCentroids &&
      currSearchLocation?.coordinates?.lat !==
        searchLocation?.coordinates?.lat &&
      currSearchLocation?.coordinates?.lon !== searchLocation?.coordinates?.lon
    ) {
      const lat = searchCentroids?.lat || 0;
      const lng = searchCentroids?.lon || 0;
      moveMapTo(lat, lng, DEFAULT_ZOOM_LEVEL);

      setMapMoved(false);
      setSelectedMarker(undefined);
      setCurrSearchLocation(searchLocation);
      setCenterCoordsLoaded(true);
    }
  }, [
    centerCoordsLoaded,
    searchCentroids,
    searchLocation,
    currSearchLocation,
    searchMapBound,
    currSearchMapBound,
    viewHotelsNearLocation,
    currViewHotelsNearLocation,
    moveMapTo,
    trackEvent,
    setCenterCoordsLoaded,
    setMapMoved,
    setInitialSearch,
    setSelectedMarker,
    setCurrSearchMapBound,
    setMapAutocompleteSearchCoordinates,
    setCurrViewHotelsNearLocation,
    fetchInitialHotelAvailability,
  ]);

  const points = useMemo(() => {
    if (!lodgings.length) {
      return [];
    }

    let lodgingsArr;

    // If Premier Collection is enabled, there may be multiple versions of the same lodging (regular vs. premier)
    if (isPremierCollectionEnabled) {
      const lodgingMap = new Map();

      lodgings.forEach((lodging) => {
        const id = lodging.lodging.id;
        if (
          !lodgingMap.has(id) ||
          lodging.lodgingCollection === LodgingCollectionEnum.Premier
        ) {
          lodgingMap.set(id, lodging);
        }
      });

      lodgingsArr = Array.from(lodgingMap.values());

      // Otherwise, just filter out the premier lodgings
    } else {
      lodgingsArr = lodgings.filter(
        (lodging) => lodging.lodgingCollection !== LodgingCollectionEnum.Premier
      );
    }

    // Then reduce the lodgings to the supercluster data structure
    const points: Array<SuperclusterPoint> = lodgingsArr.reduce(
      (acc, lodging, index) => {
        if (!lodging.available) return acc; // Skip unavailable lodgings

        acc.push({
          type: "Feature" as const,
          properties: {
            cluster: false,
            lodging: lodging,
            lodgingRank: index,
          },
          geometry: {
            type: "Point" as const,
            coordinates: [
              lodging.lodging.location.coordinates.lon,
              lodging.lodging.location.coordinates.lat,
            ],
          },
        });

        return acc;
      },
      []
    );

    return points;
  }, [lodgings, isPremierCollectionEnabled]);

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: showClustersVariant ? 95 : 0, maxZoom: 16 },
  });

  const handleMarkerClick = useCallback(
    (markerId: string, lat: number, lng: number, lodging: Lodging) => {
      setSelectedMarker(markerId);
      setSelectedMarkerLodging(lodging);

      const isHotel = !Boolean(lodging?.homesListingDetails);

      const properties: ClickedLodgingMapProperties = {
        map_funnel: MapFunnelEnum.STAYS,
        click_type: isHotel
          ? ClickedLodgingMapEnum.HOTEL
          : ClickedLodgingMapEnum.VACATION_RENTAL,
        initial_search: initialSearch,
        ...viewedHotelListProperties.properties,
        ...getLodgingTrackingProperties(lodging).properties,
        ...lodging?.trackingPropertiesV2?.properties,
      };

      const encryptedProperties = [
        ...getLodgingTrackingProperties(lodging).encryptedProperties,
        lodging?.trackingPropertiesV2?.encryptedProperties ?? "",
        ...viewedHotelListProperties.encryptedProperties,
      ];

      trackEvent({
        eventName: CLICKED_LODGING_MAP,
        properties: properties,
        encryptedProperties: encryptedProperties,
      });

      // Only pan if desktop
      if (matchesDesktop) {
        moveMapTo(lat, lng);
        mapRef.current.panBy(0, -100); // After moving the map, offset the Y position to account for the map card
      }
    },
    [initialSearch, viewedHotelListProperties]
  );

  const handleMarkerHover = useCallback((markerId: string) => {
    setHoveredMarker(markerId);
  }, []);

  const handleMarkerClose = useCallback(
    (e: React.MouseEvent<Element, MouseEvent>) => {
      e.stopPropagation(); // TODO: The onClick handlers overlap in the packages component which requires this fix. It should be fixed on the shared component.
      setSelectedMarker(undefined);
    },
    []
  );

  const renderMarker = useCallback(
    (point: SuperclusterPoint) => {
      const [longitude, latitude] = point.geometry.coordinates;
      const { lodging, lodgingRank } = point.properties;

      return (
        <ListingMarker
          key={`point-${lodging?.lodging?.id}`}
          zIndex={
            [selectedMarker, hoveredMarker, listHoveredLodgingId].includes(
              lodging?.lodging?.id
            )
              ? Z_INDEX_HOVER_OR_SELECTED_LISTING_MARKER
              : Z_INDEX_BASE_LISTING_MARKER - lodgingRank // Lodging rank is from 1-N where 1 is the highest.
          } // This prop is used by google-maps-react-markers during the rendering of the markers
          lat={latitude}
          lng={longitude}
          lodging={lodging}
          roomsCount={roomsCount}
          isVentureX={isVentureX}
          searchedNightCount={searchedNightCount || 0}
          rewardsKey={accountReferenceId || undefined}
          largestValueAccount={largestValueAccount}
          isSelected={lodging?.lodging?.id === selectedMarker}
          forceHoverState={lodging?.lodging?.id === listHoveredLodgingId}
          disableMapCard={!matchesDesktop} // Hide the map card on mobile
          onMarkerHover={handleMarkerHover} // Updates the z-Index of the marker
          onMarkerClick={(markerId, lat, lng) =>
            handleMarkerClick(markerId, lat, lng, lodging)
          }
          onMarkerClose={handleMarkerClose}
          onMarkerDetailsClick={() => {
            openHotelDetailsPage(
              lodging,
              expState,
              viewedHotelListProperties,
              mapBounds,
              searchLocationResult,
              roomsCount,
              hotelQueryParams,
              sortOrder
            );
          }}
        />
      );
    },
    [
      selectedMarker,
      hoveredMarker,
      listHoveredLodgingId,
      roomsCount,
      expState,
      handleMarkerHover,
      handleMarkerClick,
      handleMarkerClose,
    ]
  );

  const handleClusterOnClick = useCallback(
    (clusterId: number, lat: number, lng: number) => {
      let zoom =
        supercluster?.getClusterExpansionZoom(clusterId) || DEFAULT_ZOOM_LEVEL;

      trackEvent({
        eventName: CLICKED_LODGING_MAP,
        properties: {
          map_funnel: MapFunnelEnum.STAYS,
          click_type: ClickedLodgingMapEnum.CLUSTER,
          initial_search: initialSearch,
        },
      });
      moveMapTo(lat, lng, zoom);
    },
    [supercluster]
  );

  const handleClusterHover = useCallback((clusterId: number) => {
    setHoveredCluster(clusterId);
  }, []);

  const renderCluster = useCallback(
    (cluster: SuperclusterCluster) => {
      const [longitude, latitude] = cluster.geometry.coordinates;
      const { cluster_id: id, point_count: pointCount } = cluster.properties;

      const isHoveredListingInCluster = listHoveredLodgingId
        ? supercluster
            ?.getLeaves(id, Infinity, 0)
            .map((point) => point.properties.lodging.lodging.id)
            .includes(listHoveredLodgingId)
        : false;

      const isHoveredCluster = hoveredCluster === cluster.id;

      return (
        <ClusterMarker
          key={`cluster-${id}`}
          zIndex={
            isHoveredCluster
              ? Z_INDEX_HOVER_OR_CLICKED_CLUSTER_MARKER
              : Z_INDEX_CLUSTER_MARKER
          } // This prop is used by google-maps-react-markers during the rendering of the markers
          lat={latitude}
          lng={longitude}
          pointCount={pointCount}
          forceHoverState={isHoveredListingInCluster}
          clusterId={id}
          onClusterHover={handleClusterHover}
          onClusterClick={handleClusterOnClick}
        />
      );
    },
    [supercluster, hoveredCluster, listHoveredLodgingId]
  );

  const renderMarkers = useMemo(() => {
    if (!showClustersVariant) {
      const points = clusters.flatMap((cluster) => {
        if (cluster.properties.cluster) {
          const { cluster_id } = cluster.properties as ClusterProperties;

          return supercluster?.getLeaves(cluster_id) || [];
        } else {
          return cluster as SuperclusterPoint;
        }
      });

      const limitedPoints = points
        .sort((a, b) =>
          a.properties.lodgingRank < b.properties.lodgingRank ? -1 : 1
        )
        .slice(0, noClusteringMarkerLimit);

      return limitedPoints.map(renderMarker);
    } else {
      return clusters
        .flatMap((cluster) => {
          if (cluster.properties.cluster) {
            const { cluster_id, point_count } =
              cluster.properties as ClusterProperties;

            if (point_count < 4) {
              const points = supercluster?.getLeaves(cluster_id) || [];
              return points?.map((point) => {
                return renderMarker(point);
              });
            } else {
              return renderCluster(cluster as SuperclusterCluster);
            }
          } else {
            return renderMarker(cluster as SuperclusterPoint);
          }
        })
        .filter(Boolean);
    }
  }, [
    clusters,
    supercluster,
    zoom,
    showClustersVariant,
    renderCluster,
    renderMarker,
  ]);

  const handleMapOnChange = ({
    zoom,
    bounds,
  }: {
    zoom: number;
    bounds: LatLngBounds;
  }) => {
    setZoom(zoom);
    if (bounds) {
      setBounds([
        bounds.getSouthWest().lng(),
        bounds.getSouthWest().lat(),
        bounds.getNorthEast().lng(),
        bounds.getNorthEast().lat(),
      ]);
    }
  };

  const handleSearchThisArea = () => {
    if (mapRef?.current) {
      setSearchMapBound({
        northEast: {
          lat: mapRef?.current.getBounds().getNorthEast().lat(),
          lon: mapRef?.current.getBounds().getNorthEast().lng(),
        },
        southWest: {
          lat: mapRef?.current.getBounds().getSouthWest().lat(),
          lon: mapRef?.current.getBounds().getSouthWest().lng(),
        },
        LocationDescriptor: LocationDescriptorEnum.BoundingBox,
      });

      trackEvent({
        eventName: SEARCH_THIS_AREA,
        properties: {
          map_funnel: MapFunnelEnum.STAYS,
        },
      });

      fetchInitialHotelAvailability({
        history: history,
        searchFromMap: true,
        searchHotelsNear: false,
        includeHomes: true,
      });
    }
  };

  // Autocomplete value predicate
  const searchNearHereAutocompleteValuePredicate = (
    option: IResult | null,
    value: IResult | null
  ) => {
    const selection = value ? (value.id as IIdLodgings) : null;
    const opt = option ? (option.id as IIdLodgings) : null;
    return (
      !!selection &&
      !!opt &&
      selection.lodgingSelection.placeId === opt.lodgingSelection.placeId
    );
  };

  const renderMapAutocompleteSearchCoordinates = () => {
    const label = viewHotelsNearLocation?.label.split(",")[0];

    if (mapAutocompleteSearchCoordinates && label) {
      const { lat, lng } = mapAutocompleteSearchCoordinates;

      return (
        <LocationMarker
          lat={lat()}
          lng={lng()}
          label={label}
          zIndex={Z_INDEX_VIEW_STAYS_NEAR_MARKER}
        />
      );
    }
    return null;
  };

  return (
    <Box
      className="lodging-availability-cluster-map"
      width="100%"
      height="100%"
      position="relative"
    >
      <Skeleton
        variant="rect"
        height="100%"
        animation="wave"
        style={{
          display: initialMapLoaded ? "none" : "block",
        }}
      />

      {/* Map overlays */}
      {initialMapLoaded && (
        <Box>
          {/* View stays near autocomplete */}
          {matchesDesktop && (
            <Box
              position="absolute"
              top="15px"
              left="15px"
              width="300px"
              zIndex={1}
              display="flex"
              alignItems="center"
              onClick={
                () => setSelectedMarker(undefined) // Reset the selected marker when autocomplete input is focused
              }
            >
              <ViewHotelsNearLocationAutocomplete
                className="autocomplete-overrides"
                label={`View ${
                  staysSearchEnabled ? "stays" : "hotels"
                } near...`}
                isMobile={false}
                clearValue={!mapAutocompleteSearchCoordinates}
                getOptionSelected={searchNearHereAutocompleteValuePredicate}
                customIcon={
                  <Icon
                    name={IconName.MagnifyingGlass}
                    ariaLabel=""
                    aria-hidden={true}
                  />
                }
                endIcon={
                  !mapAutocompleteSearchCoordinates ? undefined : faTimesCircle
                }
                endIconOnClick={() => {
                  setMapAutocompleteSearchCoordinates(undefined);
                  fetchViewHotelsNearLocationCategories("");
                }}
              />
            </Box>
          )}

          {(mapMoved ||
            availabilityCallState !== HotelAvailabilityCallState.Complete ||
            !matchesDesktop) && (
            <Box
              position="absolute"
              display="flex"
              flexDirection="column"
              left="50%"
              bottom="4%"
              zIndex={1}
              alignItems="center"
              style={{
                transform: "translateX(-50%)",
              }}
            >
              {/* Search this area */}
              {mapMoved && (
                <Box padding="5px">
                  <ActionButton
                    defaultStyle="h4r-primary"
                    onClick={handleSearchThisArea}
                    message={
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        style={{ gap: "5px" }}
                      >
                        <Icon
                          name={IconName.MagnifyingGlass}
                          className="icon-white-fill"
                        />
                        <Typography
                          style={{ fontSize: "14px", color: "var(--white)" }}
                        >
                          Search this area
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}

              {availabilityCallState !==
                HotelAvailabilityCallState.Complete && (
                <Box display="flex" alignItems="center" padding="5px">
                  <Button
                    style={{
                      backgroundColor: "white",
                      borderRadius: "50px",
                      padding: "10px 15px",
                      border: "0.5px solid #CECFCD",
                      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      justifyContent="center"
                      style={{ gap: "8px" }}
                    >
                      <Typography style={{ fontSize: "14px" }}>
                        Loading more stays
                      </Typography>
                      <CircularProgress size="18px" />
                    </Box>
                  </Button>
                </Box>
              )}

              {!matchesDesktop && (
                <Box
                  padding={"5px"}
                  width="350px"
                  display="flex"
                  alignItems="center"
                  onClick={() => setViewHotelsNearMobileSearchOpened(true)}
                >
                  <ViewHotelsNearLocationAutocomplete
                    className={clsx("autocomplete-overrides", {
                      "disable-pointer-events": !matchesDesktop,
                    })}
                    label={`View ${
                      staysSearchEnabled ? "stays" : "hotels"
                    } near...`}
                    isMobile={true} // If true, this component does nothing. It is only used to show the input
                    clearValue={!mapAutocompleteSearchCoordinates}
                    getOptionSelected={searchNearHereAutocompleteValuePredicate}
                    customIcon={
                      <Icon
                        name={IconName.MagnifyingGlass}
                        ariaLabel=""
                        aria-hidden={true}
                      />
                    }
                    endIcon={
                      !mapAutocompleteSearchCoordinates
                        ? undefined
                        : faTimesCircle
                    }
                    endIconOnClick={() => {
                      setMapAutocompleteSearchCoordinates(undefined);
                      fetchViewHotelsNearLocationCategories("");
                      setViewHotelsNearLocation(null);
                    }}
                  />
                </Box>
              )}
            </Box>
          )}

          {!matchesDesktop && selectedMarkerLodging && (
            <Box
              className="mobile-map-card-overrides"
              position="absolute"
              bottom="0px"
              display="flex"
              flexDirection="column"
              alignItems="center"
              borderRadius="9px 9px 0px 0px"
              zIndex="2"
              width="100%"
              height="auto"
              style={{
                backgroundColor: "var(--white)",
              }}
            >
              <HotelAvailabilityMapTooltip
                lodging={selectedMarkerLodging}
                onCloseClick={(e) => {
                  e.stopPropagation();
                  setSelectedMarkerLodging(undefined);
                }}
                onClickHotelDetails={() =>
                  openHotelDetailsPage(
                    selectedMarkerLodging,
                    expState,
                    viewedHotelListProperties,
                    mapBounds,
                    searchLocationResult,
                    roomsCount,
                    hotelQueryParams,
                    sortOrder
                  )
                }
                nightCount={roomsCount}
                rewardsKey={accountReferenceId || undefined}
                isLodgingPromotionsExperiment={isLodgingPromotionsExperiment}
                isVentureX={isVentureX}
                largestValueAccount={largestValueAccount}
                onViewCarouselImage={() => {
                  trackEvent({
                    eventName: HOTEL_LIST_MAP_CAROUSEL,
                    properties: {
                      map_funnel: MapFunnelEnum.STAYS,
                    },
                  });
                }}
                policyLimit={policies?.hotels.policies[0].maxPricePerNight}
                tenant={config.TENANT}
                searchedRoomsCount={
                  "corporateTravel" in selectedMarkerLodging.lodging
                    ? roomsCount
                    : undefined
                }
                onViewCorpCompareBar={onOpenCompareBarTooltip(
                  ViewedCorpRateDescriptorEntryPoints.HOTELS_LIST_MAP
                )}
                showPolicyBanner={showPolicyBanner}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Fullscreen takeover to handle mobile variant of "Search near here" */}
      {!matchesDesktop && (
        <MobileViewHotelsNearSearch
          open={viewHotelsNearMobileSearchOpened}
          onClose={() => setViewHotelsNearMobileSearchOpened(false)}
          onComplete={(value) => {
            if (value) {
              setViewHotelsNearLocation(value);
            }
            setViewHotelsNearMobileSearchOpened(false);
          }}
        />
      )}

      <GoogleMap
        apiKey={config.googleMapsApiKey}
        options={{
          clickableIcons: false,
          zoomControl: matchesDesktop ? true : false,
          mapTypeControl: false,
          scaleControl: false,
          streetViewControl: false,
          rotateControl: false,
          fullscreenControl: false,
          styles: MAP_STYLE,
          gestureHandling: "greedy",
          // @ts-ignore
          cameraControl: false,
        }}
        onGoogleApiLoaded={onGoogleApiLoaded}
        errorContent={null}
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM_LEVEL}
        onChange={handleMapOnChange}
        // externalApiParams={{ language: getLang() }} // TODO: Not implemented
      >
        {renderMarkers}
        {mapAutocompleteSearchCoordinates &&
          renderMapAutocompleteSearchCoordinates()}
      </GoogleMap>
    </Box>
  );
};
